import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Icon } from '@ui/Icon';
import { Report, useGetScalingSize } from '@ui/Report';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';

import { OffsetBox } from './OffsetBox';
import {
  PRODUCT_GROUPS_PROPOSAL_SUMMARY_LEFT_PERCENT,
  PRODUCT_GROUPS_PROPOSAL_SUMMARY_TOP_PERCENT,
} from './report-product-groups.constants';
import type { ProductGroupsProposalSummary } from './report-product-groups.model';
import {
  PRODUCT_GROUPS_LABEL_VALUE,
  PRODUCT_GROUPS_PROPOSAL_SUMMARY,
  PRODUCT_GROUPS_TEXT_COLOR,
} from './report-product-groups.typography';

const { Text } = Report;

const PROPOSAL_SUMMARY_OFFSET_LABEL = 'total emissions';

const OFFSET_CAPTION_BY_SIGN = {
  decrease: 'Reduction from baseline',
  increase: 'Increase from baseline',
} as const;

type ProductGroupsProposalSummaryKpiProps = {
  summary: ProductGroupsProposalSummary;
};

/** Cloud + proposal total CO₂e/m², then offset vs baseline — top-right above the donut block. */
export const ProductGroupsProposalSummaryKpi: React.FC<
  ProductGroupsProposalSummaryKpiProps
> = ({ summary }) => {
  const getScalingSize = useGetScalingSize();
  const { classes } = useStyles({ getScalingSize });
  const { proposalCo2ePerGfa, offsetCo2ePerGfa } = summary;
  const offsetFontSize = PRODUCT_GROUPS_PROPOSAL_SUMMARY.offsetFontSize;
  const totalFontSize = PRODUCT_GROUPS_PROPOSAL_SUMMARY.totalFontSize;
  const caption = PRODUCT_GROUPS_PROPOSAL_SUMMARY.caption;
  const hasOffset = offsetCo2ePerGfa !== 0;
  const offsetCaption =
    offsetCo2ePerGfa < 0
      ? OFFSET_CAPTION_BY_SIGN.decrease
      : OFFSET_CAPTION_BY_SIGN.increase;

  const captionSx = {
    color: caption.color,
    lineHeight: caption.lineHeight,
    opacity: PRODUCT_GROUPS_PROPOSAL_SUMMARY.captionOpacity,
    textAlign: 'left',
    whiteSpace: 'nowrap',
  } as const;

  return (
    <Box
      className={classes.root}
      style={{
        left: `${PRODUCT_GROUPS_PROPOSAL_SUMMARY_LEFT_PERCENT}%`,
        top: `${PRODUCT_GROUPS_PROPOSAL_SUMMARY_TOP_PERCENT}%`,
      }}
    >
      <Box className={classes.valueCell} sx={{ gridColumn: 1, gridRow: 1 }}>
        <Box className={classes.co2e}>
          <Box className={classes.cloudIcon}>
            <Icon
              category="costEmission"
              icon="kgCO2e"
              size={getScalingSize(
                PRODUCT_GROUPS_PROPOSAL_SUMMARY.cloudIconSize,
              )}
            />
          </Box>
          <Text
            as="span"
            fontSize={totalFontSize}
            fontWeight={500}
            sx={{
              color: PRODUCT_GROUPS_TEXT_COLOR,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1,
              opacity: PRODUCT_GROUPS_LABEL_VALUE.opacity,
              whiteSpace: 'nowrap',
            }}
          >
            {getDisplayNumber(proposalCo2ePerGfa, {
              decimals: proposalCo2ePerGfa >= 10 ? 0 : 1,
            })}
          </Text>
        </Box>
      </Box>

      {hasOffset && (
        <Box className={classes.valueCell} sx={{ gridColumn: 2, gridRow: 1 }}>
          <Box className={classes.offset}>
            <OffsetBox
              value={offsetCo2ePerGfa}
              label={PROPOSAL_SUMMARY_OFFSET_LABEL}
              fontSize={offsetFontSize}
            />
          </Box>
        </Box>
      )}

      <Box className={classes.captionCell} sx={{ gridColumn: 1, gridRow: 2 }}>
        <Text
          as="span"
          fontSize={caption.fontSize}
          fontWeight={caption.fontWeight}
          sx={captionSx}
        >
          KgCO2e per m²
          <Box
            component="span"
            sx={{ fontSize: '0.75em', verticalAlign: 'sub' }}
          >
            GFA
          </Box>
        </Text>
      </Box>

      {hasOffset && (
        <Box className={classes.captionCell} sx={{ gridColumn: 2, gridRow: 2 }}>
          <Text
            as="span"
            fontSize={caption.fontSize}
            fontWeight={caption.fontWeight}
            sx={captionSx}
          >
            {offsetCaption}
          </Text>
        </Box>
      )}
    </Box>
  );
};

const useStyles = makeStyles<{
  getScalingSize: ReturnType<typeof useGetScalingSize>;
}>()((_theme, { getScalingSize }) => ({
  root: {
    position: 'absolute',
    display: 'grid',
    gridTemplateColumns: 'max-content max-content',
    gridTemplateRows: 'auto auto',
    alignItems: 'start',
    columnGap: getScalingSize(PRODUCT_GROUPS_PROPOSAL_SUMMARY.rowGap),
    rowGap: getScalingSize(PRODUCT_GROUPS_PROPOSAL_SUMMARY.captionGap),
    pointerEvents: 'none',
    zIndex: 1,
  },
  valueCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    alignSelf: 'end',
    justifySelf: 'start',
  },
  co2e: {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    gap: getScalingSize(PRODUCT_GROUPS_PROPOSAL_SUMMARY.co2eInnerGap),
  },
  cloudIcon: {
    display: 'flex',
    flexShrink: 0,
    opacity: PRODUCT_GROUPS_LABEL_VALUE.iconOpacity,
  },
  offset: {
    display: 'flex',
    flexShrink: 0,
    pointerEvents: 'auto',
  },
  captionCell: {
    display: 'flex',
    justifySelf: 'start',
    alignSelf: 'start',
  },
}));
